import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiTerminal, FiCode, FiCpu, FiDownload, FiPackage, FiEye, FiSearch, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { artifactoryService, HandoffPreview, HandoffReview } from '../../services/artifactoryService';
import { getAllArtifacts } from '../../services/artifactService';
import { HandoffPlugin, ProjectAsset } from '../../types/artifactory';
import { PreviewFileTree } from './PreviewFileTree';
import { ArtifactPicker, PickableArtifact } from '../artifacts/ArtifactPicker';

interface HandoffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  manifestId: string;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  terminal: <FiTerminal className="w-6 h-6" />,
  code: <FiCode className="w-6 h-6" />,
  cpu: <FiCpu className="w-6 h-6" />,
  package: <FiPackage className="w-6 h-6" />,
};

type AssetFormKind = 'snippet' | 'link' | 'artifact' | null;

const KIND_BADGE: Record<string, string> = {
  inline_snippet: 'snippet',
  external_ref: 'link',
  artifact_ref: 'artifact',
};

const genAssetId = () => `asset_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export const HandoffDialog: React.FC<HandoffDialogProps> = ({
  isOpen,
  onClose,
  manifestId,
}) => {
  const { darkMode } = useTheme();

  const [plugins, setPlugins] = useState<HandoffPlugin[]>([]);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [hydrationStatus, setHydrationStatus] = useState<any>(null);
  const [includeStale, setIncludeStale] = useState(true);
  const [includeGhost, setIncludeGhost] = useState(true);
  const [includeBuildOrder, setIncludeBuildOrder] = useState(true);
  const [includeIntentHistory, setIncludeIntentHistory] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ── Dry-run preview (always-on: runs automatically once a tool is chosen) ──
  const [preview, setPreview] = useState<HandoffPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Optional LLM deep review (advisory, never blocks download) ──
  const [review, setReview] = useState<HandoffReview | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);

  // ── Project assets (one source: manifest.assets) ──
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [allArtifacts, setAllArtifacts] = useState<PickableArtifact[]>([]);
  const [assetForm, setAssetForm] = useState<AssetFormKind>(null);
  const [isSavingAssets, setIsSavingAssets] = useState(false);
  const [fName, setFName] = useState('');
  const [fUsage, setFUsage] = useState('');
  const [fContent, setFContent] = useState('');
  const [fUrl, setFUrl] = useState('');

  const resetForm = () => { setFName(''); setFUsage(''); setFContent(''); setFUrl(''); };

  const runReview = useCallback(async () => {
    if (!selectedTool) return;
    setIsReviewing(true);
    setError(null);
    try {
      const data = await artifactoryService.reviewHandoff(manifestId, selectedTool, {
        includeStale,
        includeGhost,
        includeBuildOrder,
        includeIntentHistory,
      });
      setReview(data);
    } catch (err: any) {
      setError(err.message || 'Deep review failed');
    } finally {
      setIsReviewing(false);
    }
  }, [manifestId, selectedTool, includeStale, includeGhost, includeBuildOrder, includeIntentHistory]);

  const runPreview = useCallback(async () => {
    if (!selectedTool) return;
    setIsPreviewing(true);
    setError(null);
    try {
      const data = await artifactoryService.previewHandoff(manifestId, selectedTool, {
        includeStale,
        includeGhost,
        includeBuildOrder,
        includeIntentHistory,
      });
      setPreview(data);
      // Keep the current selection if it still exists, else select the first file.
      setSelectedFile((prev) =>
        prev && data.files.some((f) => f.path === prev) ? prev : data.files[0]?.path ?? null
      );
    } catch (err: any) {
      setError(err.message || 'Preview failed');
    } finally {
      setIsPreviewing(false);
    }
  }, [manifestId, selectedTool, includeStale, includeGhost, includeBuildOrder, includeIntentHistory]);

  // Always-on: auto-preview when a tool is selected, debounced re-preview on option change.
  useEffect(() => {
    if (!selectedTool) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runPreview();
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectedTool, includeStale, includeGhost, includeBuildOrder, includeIntentHistory, runPreview]);

  useEffect(() => {
    if (!isOpen) return;

    // Fresh dialog each open: drop any stale preview/review.
    setPreview(null);
    setSelectedFile(null);
    setReview(null);
    setAssetForm(null);
    resetForm();

    // Fetch plugins, hydration status, current assets, and the artifact catalogue.
    Promise.all([
      artifactoryService.getHandoffPlugins(),
      artifactoryService.getProjectHydrationStatus(manifestId),
      artifactoryService.getManifest(manifestId),
      getAllArtifacts(),
    ]).then(([pluginData, statusData, manifest, arts]) => {
      setPlugins(pluginData);
      setHydrationStatus(statusData);
      setAssets((manifest as any)?.assets ?? []);
      setAllArtifacts((arts ?? []).map((a: any) => ({ id: a.id, name: a.name, type: a.type })));
    }).catch((err) => {
      setError(err.message || 'Failed to load export options');
    });
  }, [isOpen, manifestId]);

  // Persist the asset list, invalidate the stale review, and re-preview the bundle.
  const commitAssets = async (next: ProjectAsset[]) => {
    setIsSavingAssets(true);
    setError(null);
    try {
      const saved = await artifactoryService.setManifestAssets(manifestId, next);
      setAssets(saved);
      setReview(null);        // any asset change invalidates a prior deep-review
      setAssetForm(null);
      resetForm();
      runPreview();           // bundle changed → refresh the always-on preview
    } catch (err: any) {
      setError(err.message || 'Failed to save assets');
    } finally {
      setIsSavingAssets(false);
    }
  };

  const addSnippet = () => {
    if (!fName || !fContent) return;
    commitAssets([...assets, {
      id: genAssetId(), kind: 'inline_snippet', name: fName, usage_note: fUsage, content: fContent,
    }]);
  };
  const addLink = () => {
    if (!fName || !fUrl) return;
    commitAssets([...assets, {
      id: genAssetId(), kind: 'external_ref', name: fName, usage_note: fUsage, url: fUrl,
    }]);
  };
  const addArtifact = (art: PickableArtifact) => {
    commitAssets([...assets, {
      id: genAssetId(), kind: 'artifact_ref', name: art.name, usage_note: '', artifact_id: art.id,
    }]);
  };
  const removeAsset = (id: string) => commitAssets(assets.filter((a) => a.id !== id));

  const handleHandoff = () => {
    if (!selectedTool) return;

    setIsExporting(true);
    setError(null);

    try {
      const url = artifactoryService.handoffProject(manifestId, selectedTool, {
        includeStale,
        includeGhost,
        includeBuildOrder,
        includeIntentHistory,
      });

      const link = document.createElement('a');
      link.href = url;
      link.download = `project_handoff_${selectedTool}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  const totalNodes = hydrationStatus
    ? (hydrationStatus.fresh_nodes || 0) + (hydrationStatus.stale_nodes || 0) + (hydrationStatus.ghost_nodes || 0) + (hydrationStatus.locked_nodes || 0) + (hydrationStatus.pending_nodes || 0) + (hydrationStatus.hydrating_nodes || 0)
    : 0;
  const hydrationPct = totalNodes > 0
    ? Math.round(((hydrationStatus?.fresh_nodes || 0) + (hydrationStatus?.locked_nodes || 0)) / totalNodes * 100)
    : 0;

  const inputCls = `w-full px-2 py-1.5 text-sm rounded border ${
    darkMode ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
  }`;
  const addBtnCls = `px-2.5 py-1 text-xs rounded-md ${
    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
  }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`w-[820px] max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className="text-lg font-semibold">Handoff to External Agent</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-600 rounded">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Tool Picker */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Target Tool
            </label>
            <div className="grid grid-cols-3 gap-3">
              {plugins.map((plugin) => (
                <button
                  key={plugin.name}
                  onClick={() => setSelectedTool(plugin.name)}
                  className={`p-3 rounded-lg border-2 text-center transition-colors ${
                    selectedTool === plugin.name
                      ? 'border-blue-500 bg-blue-500 bg-opacity-10'
                      : darkMode
                        ? 'border-gray-600 hover:border-gray-500'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-center mb-2">
                    {ICON_MAP[plugin.icon] || <FiCpu className="w-6 h-6" />}
                  </div>
                  <div className="text-sm font-medium">{plugin.display_name}</div>
                  <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plugin.instruction_filename}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Options
            </label>
            <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-750' : 'bg-gray-50'} space-y-2`}>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include stale files (with warnings)</span>
                <div className="flex items-center">
                  {hydrationStatus && (
                    <span className={`mr-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                    }`}>{hydrationStatus.stale_nodes || 0}</span>
                  )}
                  <input type="checkbox" checked={includeStale} onChange={(e) => setIncludeStale(e.target.checked)} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include ghost stubs (with TODOs)</span>
                <div className="flex items-center">
                  {hydrationStatus && (
                    <span className={`mr-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-800'
                    }`}>{hydrationStatus.ghost_nodes || 0}</span>
                  )}
                  <input type="checkbox" checked={includeGhost} onChange={(e) => setIncludeGhost(e.target.checked)} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include build order</span>
                <input type="checkbox" checked={includeBuildOrder} onChange={(e) => setIncludeBuildOrder(e.target.checked)} />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include intent history</span>
                <input type="checkbox" checked={includeIntentHistory} onChange={(e) => setIncludeIntentHistory(e.target.checked)} />
              </label>
            </div>
          </div>

          {/* Project Assets */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Project Assets
            </label>
            <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-750' : 'bg-gray-50'} space-y-2`}>
              {assets.length === 0 ? (
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No assets attached. Staple existing artifacts, links, or snippets to ship with this project.
                </p>
              ) : (
                <ul className="space-y-1">
                  {assets.map((a) => (
                    <li key={a.id} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 truncate">
                        <span className={`px-1.5 py-0.5 text-xs rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                          {KIND_BADGE[a.kind] || a.kind}
                        </span>
                        <span className="truncate">{a.name}</span>
                      </span>
                      <button
                        title={`Remove ${a.name}`}
                        disabled={isSavingAssets}
                        onClick={() => removeAsset(a.id)}
                        className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex gap-2 pt-1">
                <button type="button" className={addBtnCls} onClick={() => { setAssetForm(assetForm === 'artifact' ? null : 'artifact'); resetForm(); }}>+ Artifact</button>
                <button type="button" className={addBtnCls} onClick={() => { setAssetForm(assetForm === 'link' ? null : 'link'); resetForm(); }}>+ Link</button>
                <button type="button" className={addBtnCls} onClick={() => { setAssetForm(assetForm === 'snippet' ? null : 'snippet'); resetForm(); }}>+ Snippet</button>
              </div>

              {assetForm === 'artifact' && (
                <div className={`mt-2 rounded border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <ArtifactPicker artifacts={allArtifacts} onSelect={addArtifact} />
                </div>
              )}

              {assetForm === 'link' && (
                <div className="mt-2 space-y-2">
                  <input className={inputCls} placeholder="Asset name" value={fName} onChange={(e) => setFName(e.target.value)} />
                  <input className={inputCls} placeholder="https://…" value={fUrl} onChange={(e) => setFUrl(e.target.value)} />
                  <input className={inputCls} placeholder="How the agent should use it" value={fUsage} onChange={(e) => setFUsage(e.target.value)} />
                  <button type="button" className={addBtnCls} disabled={isSavingAssets} onClick={addLink}>Add link</button>
                </div>
              )}

              {assetForm === 'snippet' && (
                <div className="mt-2 space-y-2">
                  <input className={inputCls} placeholder="Asset name" value={fName} onChange={(e) => setFName(e.target.value)} />
                  <input className={inputCls} placeholder="How the agent should use it" value={fUsage} onChange={(e) => setFUsage(e.target.value)} />
                  <textarea className={`${inputCls} h-20 font-mono`} placeholder="Paste snippet content" value={fContent} onChange={(e) => setFContent(e.target.value)} />
                  <button type="button" className={addBtnCls} disabled={isSavingAssets} onClick={addSnippet}>Add snippet</button>
                </div>
              )}
            </div>
          </div>

          {/* Status Bar */}
          {hydrationStatus && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Project Status
              </label>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-green-500">{'●'} {hydrationStatus.fresh_nodes || 0} Fresh</span>
                  <span className="text-orange-500">{'●'} {hydrationStatus.stale_nodes || 0} Stale</span>
                  <span className="text-gray-500">{'●'} {hydrationStatus.ghost_nodes || 0} Ghost</span>
                </div>
                <div className="w-full h-2 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${hydrationPct}%` }}
                  />
                </div>
                <div className={`text-xs mt-1 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {hydrationPct}% hydrated
                </div>
              </div>
            </div>
          )}

          {/* Always-on Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Preview — exactly what the agent receives
              </label>
              <button
                onClick={runPreview}
                disabled={!selectedTool || isPreviewing}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${
                  !selectedTool || isPreviewing
                    ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                {isPreviewing ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-current" />
                ) : (
                  <FiEye className="w-4 h-4" />
                )}
                Refresh
              </button>
            </div>

            {!selectedTool && (
              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Select a target tool to preview the bundle.
              </div>
            )}

            {preview && (
              <>
                <div className={`flex gap-4 text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>{preview.files.length} files</span>
                  <span>{preview.summary.subagents} subagents</span>
                  <span>{preview.summary.hydration_pct}% hydrated</span>
                </div>
                <div className={`grid grid-cols-[260px_1fr] rounded-lg border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`h-[340px] overflow-auto border-r ${darkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-50'}`}>
                    <PreviewFileTree
                      files={preview.files}
                      selected={selectedFile}
                      onSelect={setSelectedFile}
                    />
                  </div>
                  <pre className={`h-[340px] overflow-auto p-3 text-xs whitespace-pre-wrap break-words font-mono ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'}`}>
                    {selectedFile
                      ? (preview.files.find((f) => f.path === selectedFile)?.content ?? '')
                      : 'Select a file to view its contents.'}
                  </pre>
                </div>

                {/* Optional LLM deep review — advisory, never blocks download */}
                <div className="mt-3">
                  <button
                    onClick={runReview}
                    disabled={isReviewing}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${
                      isReviewing
                        ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : darkMode ? 'bg-indigo-900/60 hover:bg-indigo-900 text-indigo-200' : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700'
                    }`}
                  >
                    {isReviewing ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-current" />
                    ) : (
                      <FiSearch className="w-4 h-4" />
                    )}
                    {isReviewing ? 'Reviewing…' : 'Deep-review before export'}
                  </button>

                  {review && (
                    <div className={`mt-2 rounded-lg border p-3 ${darkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-50'}`}>
                      {review.summary && (
                        <p className={`text-xs mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{review.summary}</p>
                      )}
                      {review.findings.length === 0 ? (
                        <p className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>No issues flagged.</p>
                      ) : (
                        <ul className="space-y-1.5 max-h-[200px] overflow-auto">
                          {review.findings.map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs">
                              <FiAlertTriangle
                                className={`mt-0.5 shrink-0 ${
                                  f.severity === 'high' ? 'text-red-500' : f.severity === 'medium' ? 'text-orange-500' : 'text-gray-400'
                                }`}
                              />
                              <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
                                <span className="font-medium">{f.file || 'bundle'}</span>
                                {' — '}{f.issue}
                                {f.suggestion && (
                                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}> ({f.suggestion})</span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Error / Success */}
          {error && (
            <div className={`text-sm rounded px-3 py-2 ${darkMode ? 'text-red-400 bg-red-900 bg-opacity-20' : 'text-red-700 bg-red-100'}`}>
              {error}
            </div>
          )}
          {success && (
            <div className={`text-sm rounded px-3 py-2 ${darkMode ? 'text-green-400 bg-green-900 bg-opacity-20' : 'text-green-700 bg-green-100'}`}>
              Handoff export started!
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 px-6 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleHandoff}
            disabled={!selectedTool || isExporting}
            className={`px-4 py-2 rounded-md flex items-center ${
              !selectedTool || isExporting
                ? 'bg-blue-800 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <FiDownload className="mr-2" />
                Handoff
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
